// AGC: Firestore Error Handler
// AGC: Silently handles transient network errors from Firestore
// AGC: Only logs critical errors that need attention

/**
 * AGC: Check if error is a transient network error that can be safely ignored
 * AGC: These errors are automatically retried by Firebase SDK
 */
export const isTransientNetworkError = (error: any): boolean => {
  if (!error) return false
  
  const errorMessage = error.message || error.toString() || ''
  const errorCode = error.code || ''
  
  // AGC: Network-related errors that Firebase handles automatically
  const transientErrors = [
    'QUIC_TOO_MANY_RTOS',
    'ERR_QUIC_PROTOCOL_ERROR',
    'ERR_INTERNET_DISCONNECTED',
    'Failed to fetch',
    'NetworkError',
    'Network request failed',
    'QUIC_CONNECTION_REFUSED',
    'QUIC_HANDSHAKE_FAILED',
  ]
  
  // AGC: Check error message
  if (transientErrors.some(err => errorMessage.includes(err))) {
    return true
  }
  
  // AGC: Check error code
  if (errorCode && transientErrors.some(err => errorCode.includes(err))) {
    return true
  }
  
  // AGC: Firebase-specific network error codes
  if (errorCode === 'unavailable' || errorCode === 'deadline-exceeded') {
    return true
  }
  
  return false
}

/**
 * AGC: Handle Firestore errors with silent logging for transient errors
 * AGC: Only logs critical errors that need developer attention
 */
export const handleFirestoreError = (
  error: any,
  context: string,
  silent: boolean = false
): void => {
  if (!error) return
  
  // AGC: If it's a transient network error, silently ignore (Firebase will retry)
  if (isTransientNetworkError(error)) {
    if (!silent) {
      // AGC: Only log in development mode for debugging
      if (import.meta.env.DEV) {
        console.debug(`🔇 AGC: Silently handling transient network error in ${context}`)
      }
    }
    return
  }
  
  // AGC: Log critical errors that need attention
  console.error(`❌ AGC: Error in ${context}:`, error)
}

/**
 * AGC: Wrap Firestore operations with error handling
 * AGC: Returns null for transient errors, throws for critical errors
 */
export const safeFirestoreOperation = async <T>(
  operation: () => Promise<T>,
  context: string,
  defaultValue: T
): Promise<T> => {
  try {
    return await operation()
  } catch (error) {
    // AGC: If transient error, return default value (Firebase will retry)
    if (isTransientNetworkError(error)) {
      handleFirestoreError(error, context, true)
      return defaultValue
    }
    
    // AGC: Critical error - log and return default
    handleFirestoreError(error, context, false)
    return defaultValue
  }
}
